import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Button, Paper } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import Header from "../components/header";
import Footer from "../components/footer";
import getApiBase from "../components/getApiBase";

const VerifyEmailChange = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [oldEmail, setOldEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [logoutLoading, setLogoutLoading] = useState(false);
  useEffect(() => {
    verifyToken();
  }, [token]);

  useEffect(() => {
    if (success && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (success && countdown === 0) {
      // Logout and redirect to login
      handleLogoutAndRedirect();
    }
  }, [success, countdown]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${getApiBase()}/api/auth/verify-email-change/${token}`, {
        method: "GET",
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        setMessage(data.message);
        setOldEmail(data.oldEmail);
        setNewEmail(data.newEmail);
      } else {
        setSuccess(false);
        setMessage(data.message || "Verification failed. The link may be invalid or expired.");
      }
    } catch (error) {
      console.error("Verification error:", error);
      setSuccess(false);
      setMessage("Failed to verify email change. Please try again later.");
    } finally {
      setLoading(false);
    }
  };



const handleLogoutAndRedirect = async () => {
  if (logoutLoading) return;

  setLogoutLoading(true);

  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    await fetch(`${getApiBase()}/api/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch (error) {
    console.error("Logout error:", error);
  } finally {
    navigate("/login");
    setLogoutLoading(false);
  }
};

  return (
    <>
      <Header />
      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f7f9fc",
          px: 2,
          py: 6,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            maxWidth: 600,
            width: "100%",
            p: 4,
            textAlign: "center",
            borderRadius: 3,
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={60} sx={{ color: "#e63926", mb: 3 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Verifying Your Email Change...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we confirm your new email address.
              </Typography>
            </>
          ) : success ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 80, color: "#059669", mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#059669", mb: 2 }}>
                Email Changed Successfully! ✅
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {message}
              </Typography>
              
              <Box sx={{ 
                backgroundColor: "#f5f5f5", 
                p: 2, 
                borderRadius: 2, 
                mb: 3,
                textAlign: "left"
              }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Old Email:</strong> <span style={{ textDecoration: "line-through", color: "#999" }}>{oldEmail}</span>
                </Typography>
                <Typography variant="body2">
                  <strong>New Email:</strong> <span style={{ color: "#059669", fontWeight: 600 }}>{newEmail}</span>
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                📧 Please use your new email address to log in from now on.
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 3, fontWeight: 600, color: "#e63926" }}>
                Logging you out and redirecting to login in {countdown} seconds...
              </Typography>
              
              <Button
  variant="contained"
  onClick={handleLogoutAndRedirect}
  disabled={logoutLoading}
  sx={{
    backgroundColor: "#e63926",
    "&:hover": { backgroundColor: "#d32f2f" },
    px: 4,
    py: 1.5,
    fontWeight: 600,
  }}
>
  {logoutLoading ? "Logging Out..." : "Go to Login Now"}
</Button>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ fontSize: 80, color: "#e63926", mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#e63926", mb: 2 }}>
                Verification Failed
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {message}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This could happen if:
              </Typography>
              <Box sx={{ textAlign: "left", mb: 3, px: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • The verification link has expired (24 hours)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • The link was already used
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • The link is invalid or corrupted
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/profile")}
                  sx={{
                    borderColor: "#e63926",
                    color: "#e63926",
                    "&:hover": { borderColor: "#d32f2f", backgroundColor: "rgba(230, 57, 38, 0.04)" },
                  }}
                >
                  Back to Profile
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate("/login")}
                  sx={{
                    backgroundColor: "#e63926",
                    "&:hover": { backgroundColor: "#d32f2f" },
                  }}
                >
                  Go to Login
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>
      <Footer />
    </>
  );
};

export default VerifyEmailChange;















